import { AccountInfo } from '@azure/msal-browser';
import { hasAccountInfo } from '../../../HubLayout/util/authUtils';
import { Capability, CapabilityState, getCapabilityState } from './capability';
import { UnauthorizedError } from '../../errors/UnauthorizedError';
import { AuthorizationFailureError } from '../../errors/AuthorizationFailureError';
import { Guid } from '../../models/Guid';

import { HubIdTokenClaims } from '../../../HubLayout/util/authUtils';

// Helper to read a claim from the account's idTokenClaims
const getClaimFromAccount = (
    user: AccountInfo,
    claimType: string
): string | undefined => {
    return (user.idTokenClaims as HubIdTokenClaims)?.[claimType] as
        | string
        | undefined;
};

const accountHasClaim = (
    user: AccountInfo,
    claimType: string,
    claimValue?: any
): boolean => {
    const claim = getClaimFromAccount(user, claimType);
    if (!claim) return false;
    if (!claimValue) return true;
    if (Array.isArray(claim)) return claim.includes(claimValue);
    return claim === claimValue;
};

//convert true/false string to capability state
// Note the difference between a false value and the value missing entirely
export const convertTrueFalseValueToCapabilityState = (
    value: string | undefined
): CapabilityState => {
    if (value) {
        if (value === 'true') {
            return CapabilityState.available;
        } else if (value === 'false') {
            return CapabilityState.unavailable;
        }
    }
    return CapabilityState.unknown;
};

//determine claim value from user account's idTokenClaims
export const getTrueFalseClaimValue = (
    user: AccountInfo | undefined,
    claimType: string
): CapabilityState => {
    if (user && hasAccountInfo(user)) {
        const claimValue = getClaimFromAccount(user, claimType);
        return convertTrueFalseValueToCapabilityState(claimValue);
    }
    return CapabilityState.unknown;
};

// Determines if a claim with the specified value is present
export const getSpecificClaimValue = (
    user: AccountInfo | undefined,
    claimType: string,
    claimValue: string
): CapabilityState => {
    if (user && hasAccountInfo(user)) {
        return accountHasClaim(user, claimType, claimValue)
            ? CapabilityState.available
            : CapabilityState.unavailable;
    }
    return CapabilityState.unknown;
};

// Returns available if the user has any of the provided role claim values on
// the standard 'roles' claim, unavailable if authenticated but lacking all of
// them, and unknown otherwise.
export const hasAnyRoleClaim = (
    user: AccountInfo | undefined,
    roleValues: readonly string[]
): CapabilityState => {
    if (!user || !hasAccountInfo(user)) {
        return CapabilityState.unknown;
    }
    for (const value of roleValues) {
        if (accountHasClaim(user, 'roles', value)) {
            return CapabilityState.available;
        }
    }
    return CapabilityState.unavailable;
};

// Check tenting for the provided user. Returns [isAuthorized, error].
// (Providing the user rather than reading from the CapabilityContext avoids a timing issue
// when this check is done before the context state is updated by AuthRequired.)
export const checkTenting = (
    user: AccountInfo | undefined
): [boolean, Error | null] => {
    if (hasAccountInfo(user)) {
        const state = { agpUser: user };
        const tents = [
            getCapabilityState(Capability.ex_tent, state),
            getCapabilityState(Capability.rx_tent, state),
        ];

        if (tents.some((t) => t === CapabilityState.available)) {
            // user is authorized so long as one tenting claim is available
            return [true, null];
        } else if (tents.some((t) => t === CapabilityState.unavailable)) {
            // user is not authorized
            return [false, new UnauthorizedError()];
        } else {
            // all claims are unknown, authorization cannot be determined
            return [false, new AuthorizationFailureError()];
        }
    } else {
        return [false, null];
    }
};

// Get authorized service tree ids from user account claims
export const getAuthorizedServiceIds = (
    isUserAuthorized: boolean,
    user: AccountInfo | undefined
): Guid[] => {
    if (user && isUserAuthorized) {
        const claimValue = getClaimFromAccount(user, 'service_tree_ids');
        if (claimValue) {
            return claimValue.split(',');
        }
        return [];
    }
    return [];
};
